import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "./ui/input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Loader2,
  CheckCircle2,
  Quote,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const NATURE_MAX = 100;

// MCA requires every company name to end with the entity suffix.
const SUFFIXES = ["PRIVATE LIMITED", "LLP", "(OPC) PRIVATE LIMITED"];

const hasValidSuffix = (raw) => {
  const clean = (raw || "").trim().toUpperCase().replace(/\s+/g, " ");
  return SUFFIXES.some((s) => clean.endsWith(s));
};

// Strip any existing suffix so users can hot-swap between Pvt Ltd ↔ LLP ↔ OPC
const stripSuffix = (raw) => {
  let s = (raw || "").trim().replace(/\s+/g, " ");
  const upper = s.toUpperCase();
  for (const suf of SUFFIXES) {
    if (upper.endsWith(suf)) {
      s = s.slice(0, s.length - suf.length).trim();
      break;
    }
  }
  return s;
};

const EMPTY_FORM = {
  primary_name: "",
  option_2: "",
  option_3: "",
  nature_of_business: "",
};

const EMPTY_MODAL = {
  founder1: { name: "", dob: "" },
  founder2: { name: "", dob: "" },
  phone: "",
  email: "",
  city: "",
};

export default function NameCheck() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [contact, setContact] = useState(EMPTY_MODAL);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [step, setStep] = useState("contact"); // "contact" | "founders"

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const onCheckNames = (e) => {
    e.preventDefault();
    if (!form.primary_name.trim()) {
      toast.error("Please enter your preferred company name.");
      return;
    }
    if (!form.option_2.trim()) {
      toast.error("Please enter at least one alternative name.");
      return;
    }
    // Every non-empty name must end with a valid MCA suffix
    const namesToCheck = [
      { label: "Preferred Company Name", value: form.primary_name },
      { label: "Alternative Name 2", value: form.option_2 },
      { label: "Alternative Name 3", value: form.option_3 },
    ];
    for (const n of namesToCheck) {
      if (n.value.trim() && !hasValidSuffix(n.value)) {
        toast.error(
          `${n.label} must end with PRIVATE LIMITED, LLP, or (OPC) PRIVATE LIMITED.`
        );
        return;
      }
    }
    if (!form.nature_of_business.trim()) {
      toast.error("Please tell us the nature of your business.");
      return;
    }
    setStep("contact");
    setModalOpen(true);
  };

  const goToFounders = (e) => {
    e.preventDefault();
    if (contact.phone.replace(/\D/g, "").length !== 10) {
      toast.error("Please enter a valid 10-digit mobile number.");
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(contact.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setStep("founders");
  };

  const submit = async ({ withCompatibility }) => {
    if (!contact.founder1.name.trim() || !contact.founder1.dob) {
      toast.error("Please enter Founder 1's name and date of birth.");
      return;
    }
    if (contact.phone.replace(/\D/g, "").length < 7) {
      toast.error("Please enter a valid mobile number.");
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(contact.email)) {
      toast.error("Please enter a valid email.");
      return;
    }

    const founders = [
      { name: contact.founder1.name.trim(), dob: contact.founder1.dob.trim() },
    ];
    if (contact.founder2.name.trim() && contact.founder2.dob) {
      founders.push({
        name: contact.founder2.name.trim(),
        dob: contact.founder2.dob.trim(),
      });
    }

    const name_options = [form.primary_name, form.option_2, form.option_3]
      .map((s) => s.trim())
      .filter(Boolean);

    const baseNature = form.nature_of_business.trim();
    const entityTag = " | ENTITY: Pvt Ltd / LLP / (OPC) Pvt. Ltd. (default)";
    const tag = withCompatibility
      ? " | UPSELL: Founder-Business Name Compatibility Analysis (₹3,000) requested."
      : "";

    const payload = {
      name_options,
      domain_options: [],
      trademark_options: [],
      nature_of_business: baseNature + entityTag + tag,
      phone: contact.phone.trim(),
      email: contact.email.trim().toLowerCase(),
      city: contact.city.trim(),
      founders,
    };

    setSubmitting(true);
    try {
      await axios.post(`${API}/name-check`, payload);
      setSubmitted(true);
      setModalOpen(false);
      setStep("contact");
      setForm(EMPTY_FORM);
      setContact(EMPTY_MODAL);
      toast.success(
        withCompatibility
          ? "Got it! Our team will reach out with payment instructions for your ₹3,000 compatibility analysis within 24 hours."
          : "✅ Your report is on its way! Check your WhatsApp & Email within 24 hours."
      );
    } catch (err) {
      toast.error("Couldn't submit right now. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) return <SuccessState onReset={() => setSubmitted(false)} />;

  return (
    <section id="name-check" className="bg-white" data-testid="name-check-section">
      {/* Slim Hero */}
      <div className="border-b border-black/10 bg-[#FEF9C3]" data-testid="name-check-quote">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 text-center">
          <Quote
            size={28}
            className="mx-auto text-[#EAB308] opacity-80"
            strokeWidth={1.5}
          />
          <p
            className="mt-4 font-heading font-black italic tracking-tight leading-[0.98] text-black"
            style={{ fontSize: "clamp(1.75rem, 5.5vw, 4rem)" }}
            data-testid="quote-shakespeare"
          >
            &ldquo;What&apos;s In A Name?&rdquo;{" "}
            <span className="not-italic font-normal text-base sm:text-sm tracking-[0.32em] uppercase text-black/60 align-middle ml-2">
              — William Shakespeare
            </span>
          </p>
          <p
            className="mt-5 font-heading font-black tracking-tight leading-[1.02] text-black"
            style={{ fontSize: "clamp(1.5rem, 4.2vw, 3.25rem)" }}
            data-testid="quote-punchline"
          >
            When it comes to{" "}
            <span className="relative inline-block">
              <span className="relative z-10 px-2">BUSINESS</span>
              <span className="absolute inset-x-0 top-[15%] bottom-[12%] bg-[#EAB308]/85 -skew-y-1" />
            </span>{" "}
            — it&apos;s{" "}
            <span className="text-[#EAB308] drop-shadow-[0_0_1px_rgba(0,0,0,0.35)]">
              EVERYTHING.
            </span>
          </p>
        </div>
      </div>

      {/* Form */}
      <div id="nc-form" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 scroll-mt-24">
        <div className="border-2 border-black bg-white p-6 sm:p-10">
          <div className="text-center">
            <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight" data-testid="name-check-h1">
              Check Your Company Name Availability
            </h1>
            <p className="mt-3 text-black/70 text-base sm:text-lg leading-relaxed">
              Enter up to 3 name choices — we&apos;ll check{" "}
              <b>MCA, Trademark &amp; Domain Availability</b>.
            </p>
          </div>

          {/* MCA suffix requirement banner */}
          <div
            className="mt-6 border-l-2 border-[#EAB308] bg-[#FEF9C3]/60 px-4 py-3"
            data-testid="mca-suffix-banner"
          >
            <p className="text-xs sm:text-sm text-black/85 leading-relaxed">
              <b className="font-heading tracking-wide uppercase text-black">MCA Rule:</b>{" "}
              Every name must end with{" "}
              <b className="text-black">PRIVATE LIMITED</b>,{" "}
              <b className="text-black">LLP</b>, or{" "}
              <b className="text-black">(OPC) PRIVATE LIMITED</b>. Use the quick-add
              chips below each field.
            </p>
          </div>

          <form onSubmit={onCheckNames} data-testid="name-check-form" className="mt-6 space-y-6">
            <FieldRow
              label="Preferred Company Name"
              required
              testId="name-primary"
              placeholder="e.g. ZOZO VENTURE PRIVATE LIMITED"
              helper="Tip: Adding alternative names increases your chances of approval"
              value={form.primary_name}
              onChange={(v) => setField("primary_name", v)}
              suffixHelper
            />

            <FieldRow
              label="Alternative Name 2"
              required
              testId="name-option-2"
              placeholder="e.g. ZOZO VENTURES LLP"
              value={form.option_2}
              onChange={(v) => setField("option_2", v)}
              suffixHelper
            />

            <FieldRow
              label="Alternative Name 3"
              optional
              testId="name-option-3"
              placeholder="e.g. ZO ZO VENTURE (OPC) PRIVATE LIMITED"
              value={form.option_3}
              onChange={(v) => setField("option_3", v)}
              suffixHelper
            />

            {/* Nature of Business */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label htmlFor="nature-of-business" className="text-xs font-semibold tracking-[0.15em] uppercase text-black/70">
                  Nature of Business <span className="text-[#EAB308]">*</span>
                </label>
                <span className="text-[10px] text-black/40 tracking-wider uppercase">
                  {form.nature_of_business.length}/{NATURE_MAX}
                </span>
              </div>
              <Input
                id="nature-of-business"
                data-testid="nature-of-business"
                value={form.nature_of_business}
                onChange={(e) => setField("nature_of_business", e.target.value.slice(0, NATURE_MAX))}
                placeholder="e.g. IT Services, Food & Beverage, EdTech"
                maxLength={NATURE_MAX}
                className="rounded-none border-black/30 focus-visible:ring-0 focus-visible:border-black h-11"
              />
              <p
                className="mt-2 flex items-center gap-2 text-[11px] text-black/60"
                data-testid="default-entity-note"
              >
                <span className="inline-flex items-center gap-1.5 border border-[#EAB308]/60 bg-[#FEF9C3] px-2 py-0.5 font-semibold tracking-wide text-black">
                  <span className="inline-block w-1.5 h-1.5 bg-[#EAB308]" />
                  Default entity
                </span>
                <span>
                  We&apos;ll check for <b className="text-black">Pvt Ltd / LLP / (OPC) Pvt. Ltd.</b>{" "}
                  — mention above if you want a different structure.
                </span>
              </p>
            </div>

            <div className="pt-1">
              <button
                type="submit"
                data-testid="check-name-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#EAB308] text-white px-6 py-4 font-heading font-bold text-lg tracking-wide uppercase hover:bg-black transition-colors"
              >
                Check Availability — Free Report →
              </button>
              <ul
                className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[11px] font-medium text-black/70"
                data-testid="name-check-reassurance"
              >
                <li className="inline-flex items-center gap-1">
                  <CheckCircle2 size={12} className="text-[#EAB308]" strokeWidth={3} />
                  100% Free
                </li>
                <li className="inline-flex items-center gap-1">
                  <CheckCircle2 size={12} className="text-[#EAB308]" strokeWidth={3} />
                  24-hour report
                </li>
                <li className="inline-flex items-center gap-1">
                  <CheckCircle2 size={12} className="text-[#EAB308]" strokeWidth={3} />
                  MCA + Trademark + Domain
                </li>
                <li className="inline-flex items-center gap-1">
                  <CheckCircle2 size={12} className="text-[#EAB308]" strokeWidth={3} />
                  No obligation
                </li>
              </ul>
              <p className="mt-2 text-center text-[10px] text-black/45" data-testid="name-check-trust">
                🔒 Your information is safe with us
              </p>
            </div>
          </form>
        </div>
      </div>

      {/* Two-step modal: Contact → Founders */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          className="sm:max-w-lg rounded-none border border-black/15 shadow-2xl p-0 gap-0 max-h-[92vh] overflow-y-auto [&>button]:hidden"
        >
          {step === "contact" ? (
            <ContactStep
              contact={contact}
              setContact={setContact}
              onContinue={goToFounders}
            />
          ) : (
            <FoundersStep
              contact={contact}
              setContact={setContact}
              submit={submit}
              submitting={submitting}
              onBack={() => setStep("contact")}
            />
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

function ContactStep({ contact, setContact, onContinue }) {
  return (
    <form onSubmit={onContinue} data-testid="modal-step-contact" className="px-6 sm:px-8 py-7 sm:py-9">
      <div className="text-center">
        <div
          className="mx-auto w-14 h-14 rounded-full bg-[#FEF9C3] flex items-center justify-center text-2xl"
          aria-hidden
        >
          📩
        </div>
        <DialogHeader className="mt-4 text-center">
          <DialogTitle
            className="font-heading font-black tracking-tight leading-[1.1] text-center text-black"
            style={{ fontSize: "clamp(1.4rem, 4.4vw, 1.85rem)" }}
            data-testid="modal-step-contact-title"
          >
            Where Should We Send Your Report?
          </DialogTitle>
        </DialogHeader>
        <p className="mt-3 text-sm sm:text-base text-black/70 leading-relaxed">
          Your <b>Name Availability Report</b> across <b>MCA, Trademark &amp; Domain</b>{" "}
          will be on your <b>WhatsApp/Email ASAP</b>.
        </p>
      </div>

      <div className="mt-6 space-y-4">
        <div>
          <label className="block text-[11px] font-semibold tracking-[0.15em] uppercase text-black/70 mb-1.5">
            Mobile Number <span className="text-[#EAB308]">*</span>
          </label>
          <div className="flex items-stretch">
            <span className="inline-flex items-center px-3 border border-black/30 border-r-0 bg-[#F4F4F5] text-sm">
              +91
            </span>
            <Input
              data-testid="modal-phone"
              value={contact.phone}
              onChange={(e) => {
                const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 10);
                setContact((c) => ({ ...c, phone: digitsOnly }));
              }}
              placeholder="98765 43210"
              inputMode="numeric"
              className="rounded-none border-black/30 focus-visible:ring-0 focus-visible:border-black h-11 flex-1"
            />
          </div>
        </div>
        <div>
          <label className="block text-[11px] font-semibold tracking-[0.15em] uppercase text-black/70 mb-1.5">
            Email ID <span className="text-[#EAB308]">*</span>
          </label>
          <Input
            type="email"
            data-testid="modal-email"
            value={contact.email}
            onChange={(e) => setContact((c) => ({ ...c, email: e.target.value }))}
            placeholder="you@example.com"
            className="rounded-none border-black/30 focus-visible:ring-0 focus-visible:border-black h-11"
          />
        </div>
        <div>
          <label className="block text-[11px] font-semibold tracking-[0.15em] uppercase text-black/70 mb-1.5">
            City
          </label>
          <Input
            data-testid="modal-city"
            value={contact.city}
            onChange={(e) => setContact((c) => ({ ...c, city: e.target.value }))}
            placeholder="Mumbai, Bengaluru, Delhi NCR…"
            className="rounded-none border-black/30 focus-visible:ring-0 focus-visible:border-black h-11"
          />
        </div>
      </div>

      <button
        type="submit"
        data-testid="modal-send-report"
        className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#EAB308] text-white px-6 py-3.5 font-heading font-bold tracking-wide uppercase hover:bg-black transition-colors"
      >
        Send My Report →
      </button>
      <p className="mt-3 text-center text-xs text-black/55" data-testid="modal-contact-trust">
        🔒 Your details are safe. No spam, ever.
      </p>
    </form>
  );
}

function FoundersStep({ contact, setContact, submit, submitting, onBack }) {
  return (
    <div data-testid="modal-step-founders" className="px-0 pt-0 pb-6 sm:pb-8">
      {/* Stat banner */}
      <div className="bg-[#FEF9C3] border-l-[6px] border-[#EAB308] px-6 sm:px-8 py-6 sm:py-7" data-testid="modal-attention-banner">
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white border border-[#EAB308]/40 text-[10px] sm:text-[11px] font-bold tracking-[0.22em] uppercase text-[#A16207]">
          Did You Know? <span aria-hidden>💡</span>
        </div>
        <p className="mt-3 text-[10px] sm:text-[11px] font-bold tracking-[0.28em] uppercase text-[#A16207]">
          FY 2025–26
        </p>
        <p
          className="mt-1 font-heading font-black tracking-tight leading-[0.95] text-black"
          style={{ fontSize: "clamp(2.25rem, 8vw, 3.5rem)" }}
          data-testid="modal-stat-line"
        >
          <span className="text-[#EAB308]">3,43,623</span>
        </p>
        <p className="mt-1 text-sm sm:text-base font-semibold text-black/80 leading-snug">
          New Companies &amp; LLPs registered in India.
        </p>

        <div className="mt-5 pt-5 border-t border-[#EAB308]/40">
          <p
            className="font-heading font-black leading-[1.1] tracking-tight text-black"
            style={{ fontSize: "clamp(1.25rem, 4vw, 1.75rem)" }}
            data-testid="modal-fail-line"
          >
            But{" "}
            <span className="relative inline-block whitespace-nowrap">
              <span className="relative z-10 px-1 text-[#EAB308]">90%</span>
              <span className="absolute inset-x-0 bottom-0.5 h-3 bg-[#EAB308]/35 -skew-x-6" />
            </span>{" "}
            of Startups fail within the first few years.
          </p>
        </div>
      </div>

      {/* Insight headline */}
      <div className="px-6 sm:px-8 pt-7 sm:pt-8">
        <DialogHeader className="text-center">
          <DialogTitle
            className="font-heading font-black tracking-tight leading-[1.05] text-center text-black"
            style={{ fontSize: "clamp(1.4rem, 4.4vw, 2rem)" }}
          >
            A Founder&apos;s{" "}
            <span className="relative inline-block">
              <span className="relative z-10 px-1.5">Name</span>
              <span className="absolute inset-x-0 top-[15%] bottom-[12%] bg-[#EAB308]/85 -skew-y-1" />
            </span>{" "}
            &amp;{" "}
            <span className="relative inline-block">
              <span className="relative z-10 px-1.5">Date of Birth</span>
              <span className="absolute inset-x-0 top-[15%] bottom-[12%] bg-[#EAB308]/85 -skew-y-1" />
            </span>{" "}
            also influence Business Success.
          </DialogTitle>
        </DialogHeader>
      </div>

      {/* Founder fields */}
      <div className="px-6 sm:px-8 mt-6 space-y-4">
        <FounderBlock
          idx={1}
          required
          value={contact.founder1}
          onChange={(v) => setContact((c) => ({ ...c, founder1: v }))}
        />
        <FounderBlock
          idx={2}
          value={contact.founder2}
          onChange={(v) => setContact((c) => ({ ...c, founder2: v }))}
        />
      </div>

      {/* Upsell highlight box */}
      <div className="px-6 sm:px-8 mt-6">
        <div className="border border-[#EAB308] bg-[#FEF9C3] px-4 py-3.5 text-sm leading-relaxed text-black/85" data-testid="modal-upsell-box">
          Add a <b>Founder–Business Name Compatibility Analysis</b> for{" "}
          <span className="text-[#EAB308] font-bold">₹3,000</span> to ensure
          success alignment.
        </div>
      </div>

      <DialogFooter className="flex-col-reverse sm:flex-row sm:justify-stretch gap-2 px-6 sm:px-8 mt-5 pb-1">
        <button
          type="button"
          data-testid="modal-skip"
          onClick={() => submit({ withCompatibility: false })}
          disabled={submitting}
          className="inline-flex items-center justify-center gap-2 border border-black px-4 py-3 font-semibold hover:bg-black hover:text-white transition-colors disabled:opacity-50 sm:flex-1 text-sm"
        >
          Skip — Send the Report Only
        </button>
        <button
          type="button"
          data-testid="modal-submit-paid"
          onClick={() => submit({ withCompatibility: true })}
          disabled={submitting}
          className="inline-flex items-center justify-center gap-2 bg-[#EAB308] text-white px-4 py-3.5 font-heading font-bold tracking-wide uppercase hover:bg-black transition-colors disabled:opacity-50 sm:flex-[1.4] text-sm"
        >
          {submitting ? (
            <>
              <Loader2 className="animate-spin" size={16} /> Submitting…
            </>
          ) : (
            <>✅ Yes, Add Compatibility (₹3,000)</>
          )}
        </button>
      </DialogFooter>

      <button
        type="button"
        onClick={onBack}
        data-testid="modal-back"
        className="mt-3 mx-auto block text-[11px] text-black/45 hover:text-black/75 underline underline-offset-4"
      >
        ← Edit contact details
      </button>
    </div>
  );
}

function FieldRow({ label, required, optional, testId, placeholder, helper, value, onChange, suffixHelper }) {
  const trimmed = (value || "").trim();
  const isValid = hasValidSuffix(trimmed);
  const showChips = suffixHelper && trimmed.length > 0 && !isValid;
  const applySuffix = (suffix) => {
    const base = stripSuffix(value);
    onChange(base ? `${base} ${suffix}` : suffix);
  };

  return (
    <div>
      <label htmlFor={testId} className="block text-xs font-semibold tracking-[0.15em] uppercase text-black/70 mb-1.5">
        {label}
        {required && <span className="text-[#EAB308]"> *</span>}
        {optional && (
          <span className="text-black/45 normal-case tracking-normal ml-1.5 font-normal">(Optional)</span>
        )}
      </label>
      <Input
        id={testId}
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={120}
        className={`rounded-none border-black/30 focus-visible:ring-0 focus-visible:border-black h-11 ${
          suffixHelper && trimmed.length > 0
            ? isValid
              ? "border-green-600 focus-visible:border-green-600"
              : "border-orange-500 focus-visible:border-orange-500"
            : ""
        }`}
      />

      {/* MCA suffix quick-add row */}
      {suffixHelper && (
        <div className="mt-2 flex flex-wrap items-center gap-2" data-testid={`${testId}-suffix-row`}>
          {trimmed.length === 0 ? (
            <span className="text-[11px] text-black/50">
              End with:
            </span>
          ) : isValid ? (
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-green-700" data-testid={`${testId}-valid`}>
              <CheckCircle2 size={13} strokeWidth={2.5} /> Valid MCA suffix
            </span>
          ) : (
            <span className="text-[11px] font-semibold text-orange-600" data-testid={`${testId}-missing`}>
              Missing suffix — add one:
            </span>
          )}
          {(!isValid) && SUFFIXES.map((suf) => (
            <button
              key={suf}
              type="button"
              data-testid={`${testId}-suffix-${suf.replace(/[^A-Z]/g, "")}`}
              onClick={() => applySuffix(suf)}
              className="inline-flex items-center gap-1 border border-black/25 bg-white px-2 py-1 text-[11px] font-semibold text-black hover:bg-black hover:text-white transition-colors"
            >
              + {suf}
            </button>
          ))}
        </div>
      )}

      {helper && (
        <p className="mt-1.5 text-[11px] text-black/55 italic" data-testid={`${testId}-helper`}>
          {helper}
        </p>
      )}
    </div>
  );
}

function FounderBlock({ idx, required, value, onChange }) {
  return (
    <div className="border border-black/15 p-4" data-testid={`modal-founder-${idx}`}>
      <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-black/70 mb-3">
        Founder {idx} {required ? <span className="text-[#EAB308]">*</span> : <span className="text-black/45 normal-case tracking-normal text-[11px]">(Optional)</span>}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] font-semibold tracking-[0.15em] uppercase text-black/60 mb-1.5">
            Full Name
          </label>
          <Input
            data-testid={`modal-founder-name-${idx}`}
            value={value.name}
            onChange={(e) => onChange({ ...value, name: e.target.value })}
            placeholder="Full name"
            className="rounded-none border-black/30 focus-visible:ring-0 focus-visible:border-black h-11"
          />
        </div>
        <div>
          <label className="block text-[10px] font-semibold tracking-[0.15em] uppercase text-black/60 mb-1.5">
            Date of Birth
          </label>
          <DobPicker
            value={value.dob}
            onChange={(v) => onChange({ ...value, dob: v })}
            testIdPrefix={`modal-dob-${idx}`}
          />
        </div>
      </div>
    </div>
  );
}

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function DobPicker({ value, onChange, testIdPrefix }) {
  const parts = (value || "").split("-");
  const [year, setYear] = React.useState(parts[0] || "");
  const [month, setMonth] = React.useState(parts[1] || "");
  const [day, setDay] = React.useState(parts[2] || "");

  // Keep a ref to the latest onChange so we don't need it in deps below
  // (parent typically passes a fresh inline function each render).
  const onChangeRef = React.useRef(onChange);
  React.useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  React.useEffect(() => {
    if (year && month && day) onChangeRef.current(`${year}-${month}-${day}`);
    else if (!year && !month && !day) onChangeRef.current("");
  }, [year, month, day]);

  const currentYear = new Date().getFullYear();
  const years = [];
  for (let y = currentYear; y >= 1930; y--) years.push(String(y));

  const daysInMonth = (() => {
    if (!year || !month) return 31;
    return new Date(parseInt(year, 10), parseInt(month, 10), 0).getDate();
  })();
  const days = [];
  for (let d = 1; d <= daysInMonth; d++) days.push(String(d).padStart(2, "0"));

  // Functional setDay avoids `day` in deps and prevents infinite loop.
  React.useEffect(() => {
    setDay((current) =>
      current && parseInt(current, 10) > daysInMonth
        ? String(daysInMonth).padStart(2, "0")
        : current
    );
  }, [daysInMonth]);

  return (
    <div className="grid grid-cols-3 gap-1.5" data-testid={testIdPrefix}>
      <Select value={day} onValueChange={setDay}>
        <SelectTrigger data-testid={`${testIdPrefix}-day`} className="rounded-none border-black/30 focus:ring-0 focus:border-black h-11 text-sm">
          <SelectValue placeholder="Day" />
        </SelectTrigger>
        <SelectContent className="rounded-none max-h-64">
          {days.map((d) => (<SelectItem key={d} value={d}>{parseInt(d, 10)}</SelectItem>))}
        </SelectContent>
      </Select>
      <Select value={month} onValueChange={setMonth}>
        <SelectTrigger data-testid={`${testIdPrefix}-month`} className="rounded-none border-black/30 focus:ring-0 focus:border-black h-11 text-sm">
          <SelectValue placeholder="Month" />
        </SelectTrigger>
        <SelectContent className="rounded-none max-h-64">
          {MONTHS.map((m, i) => (<SelectItem key={m} value={String(i + 1).padStart(2, "0")}>{m.slice(0, 3)}</SelectItem>))}
        </SelectContent>
      </Select>
      <Select value={year} onValueChange={setYear}>
        <SelectTrigger data-testid={`${testIdPrefix}-year`} className="rounded-none border-black/30 focus:ring-0 focus:border-black h-11 text-sm">
          <SelectValue placeholder="Year" />
        </SelectTrigger>
        <SelectContent className="rounded-none max-h-64">
          {years.map((y) => (<SelectItem key={y} value={y}>{y}</SelectItem>))}
        </SelectContent>
      </Select>
    </div>
  );
}

function SuccessState({ onReset }) {
  return (
    <section id="name-check" className="border-b border-black/10 bg-white" data-testid="name-check-success">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 text-center">
        <div className="mx-auto w-14 h-14 bg-[#EAB308] flex items-center justify-center">
          <CheckCircle2 size={28} className="text-white" strokeWidth={2.5} />
        </div>
        <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tight mt-6">
          Your name analysis is on its way.
        </h2>
        <p className="mt-5 text-lg text-black/70">
          A ZOZO advisor will send the complete analysis — MCA Availability,
          Domains, Trademarks and any compatibility add-ons — to your email.
        </p>
        <button
          onClick={onReset}
          data-testid="name-check-reset"
          className="mt-7 border border-black px-6 py-3 font-semibold hover:bg-black hover:text-white transition-colors"
        >
          Check another name
        </button>
      </div>
    </section>
  );
}
